import { useRef, useState, type FC } from "react"
import { Card } from "../../../../components"
import { ProfileCardNextGrade, ProfileCourseList, ProfileLevel } from ".."
import { useGetVolunteerLevelListQuery } from "presentation/store/services/volunteerApi"
import "./index.scss"
import { profileDataDefault, useGetProfileDataQuery } from "presentation/store/services/profileApi"
import { type ProgressForLevelPercentage } from "presentation/store/model/user"
import { buildCertificateData } from "presentation/services/certificate/data"
import { openPdfInNewTab } from "presentation/services/certificate/openPdf"

type LevelTasks = Array<{
    title: string
    type: string
    completed: boolean | number[]
    percentage?: ProgressForLevelPercentage
}> | undefined

const ProfileMyGrade: FC = () => {
    const generating = useRef(false)
    const [isGenerating, setIsGenerating] = useState(false)
    const [certificateError, setCertificateError] = useState("")
    const { data: profileData = profileDataDefault, isLoading: loadingProfile, isError: profileError, } =
        useGetProfileDataQuery("")
    const { data: levelsData, isLoading: loadingLevels, isError: levelsError, } = useGetVolunteerLevelListQuery("")

    const hours = profileData.statistics.verifiedHours
    const events = profileData.statistics.eventsCount
    let myLevelIdx = -1
    let myLevelName = "Загрузка..."
    let levelTasks: LevelTasks =
        ["Загружаем...", "Задания...", "Скоро будут :)"]
            .map(title => ({ title, })) as LevelTasks

    if (!loadingLevels && !loadingProfile && !profileError && !levelsError && levelsData) {
        myLevelIdx = levelsData.findIndex(lvl => lvl.id === profileData?.user.volunteerLevelId)
        myLevelName = myLevelIdx >= 0
            ? levelsData[myLevelIdx].name
            : "I уровень совсем скоро! :)"
        const nextLevel = levelsData[myLevelIdx + 1]
        levelTasks = nextLevel
            ? profileData.progressForLevels
                .find(lvl => lvl.id === nextLevel.id)
                ?.requirements
                .map(req => ({
                    ...req,
                    title: nextLevel.requirements.find(requirement => requirement.type === req.type)?.title || req.type,
                }))
            : []
    }

    const handleCertificateClick = async (): Promise<void> => {
        if (generating.current || !levelsData || myLevelIdx < 0) return
        generating.current = true
        setIsGenerating(true)
        setCertificateError("")
        try {
            await openPdfInNewTab(async () => {
                const data = buildCertificateData(profileData, levelsData)
                const { generateCertificatePdf, } = await import("presentation/services/certificate")
                return await generateCertificatePdf(data)
            })
        } catch (error) {
            setCertificateError(error instanceof Error ? error.message : "Не удалось сформировать сертификат.")
        } finally {
            generating.current = false
            setIsGenerating(false)
        }
    }

    return (
        <section className="profile-my-grade">
            <h2 className="profile-my-grade__header">Мой рост</h2>
            <div className="profile-my-grade__top">
                <Card className="profile-my-grade__level">
                    <ProfileLevel level={myLevelIdx + 1}/>
                    <div className="profile-my-grade__my-level">
                        <p className="profile-my-grade__sub-title">Мой уровень</p>
                        <p className="profile-my-grade__title">
                            {
                                myLevelName
                            }
                        </p>
                    </div>
                </Card>
                <div className="profile-my-grade__right" >
                    <Card className="profile-my-grade__card">
                        <p className="profile-my-grade__sub-title">Часов волонтерства</p>
                        <p className="profile-my-grade__title">{hours}</p>
                    </Card>
                    <Card className="profile-my-grade__card">
                        <p className="profile-my-grade__sub-title">Мероприятий посетил</p>
                        <p className="profile-my-grade__title">{events}</p>
                    </Card>
                    <Card className="profile-my-grade__card profile-my-grade__certificate">
                        <p className="profile-my-grade__sub-title">Сертификат волонтёра</p>
                        <button
                            type="button"
                            className="profile-my-grade__button"
                            data-has-certificate={myLevelIdx >= 0}
                            aria-label="Открыть сертификат в новой вкладке"
                            aria-busy={isGenerating}
                            disabled={isGenerating}
                            onClick={() => { void handleCertificateClick() }}
                        >
                            {isGenerating ? "Формируем…" : "Скачать"}
                        </button>
                    </Card>

                </div>
            </div>
            {certificateError && <p className="profile-my-grade__certificate-error" role="alert">{certificateError}</p>}
            <div className="profile-my-grade__bottom">
                <ProfileCardNextGrade levelTasks={levelTasks} />
                <ProfileCourseList />
            </div>
        </section>
    )
}

export default ProfileMyGrade
